import ConfirmModal from 'components/ConfirmModal';
import DrawerMenu from 'components/DrawerMenu';
import Form, { FormDataModel, FormModel } from 'components/Form';
import PageLoader from 'components/PageLoader';
import { FC, RefObject, useEffect, useRef, useState } from 'react';
import { getDrawerWidth } from 'utils/validators/HelperFunctions';
import { removeErrorFieldsFromValues } from 'utils/validators';
import { Box, Button, Grid, Stack, useMediaQuery, useTheme } from '@mui/material';
import strings from 'common/Translation/Translate';

type MasterDataEntityDrawerProps = {
  open: boolean;
  title: string;
  isEdit: boolean;
  formModel: FormModel[];
  formValues: FormDataModel;
  loading?: boolean;
  saving?: boolean;
  canDelete?: boolean;
  onClose: () => void;
  onSave: (body: FormDataModel) => void | Promise<void>;
  onDelete?: () => void;
  /**
   * When set, Delete button opens this flow instead of the built-in ConfirmModal
   * (e.g. user delete → reassignment in shared Delete modal).
   */
  onRequestDelete?: (entityName: string) => void;
  onInvalid?: (message: string) => void;
  children?: React.ReactNode;
  saveLabel?: string;
  width?: number | string;
  skipValidation?: boolean;
  /** Optional explicit name for delete confirmation; otherwise derived from formValues. */
  deleteEntityName?: string;
  /** Dropdown options keyed by field name, forwarded to the underlying Form. */
  dropdownData?: any;
  /** Fired on every field change, forwarded from the underlying Form's onChange. */
  onFieldChange?: (field: string, value: any, formData: FormDataModel) => void;
  /** Rendered above the form, e.g. an enabled/disabled Switch when editing. */
  headerExtra?: React.ReactNode;
};

const resolveEntityName = (values: FormDataModel = {}, explicit?: string) => {
  if (explicit?.trim()) return explicit.trim();
  if (typeof values.name === 'string' && values.name.trim()) return values.name.trim();
  const localizedNameKey = Object.keys(values).find(
    key => key.startsWith('name_') && typeof values[key] === 'string' && String(values[key]).trim(),
  );
  if (localizedNameKey) return String(values[localizedNameKey]).trim();
  const knownKeys = [
    'fullName',
    'firstName',
    'addOnName',
    'layoutName',
    'eventTypeName',
    'venueTypeName',
    'roomTypeName',
    'mealPlanName',
    'pricePackageName',
    'locationName',
    'industryTypeName',
    'surchargeName',
    'taxCodeName',
    'taxGroupName',
  ];
  for (const key of knownKeys) {
    if (typeof values[key] === 'string' && values[key].trim()) return values[key].trim();
  }
  return '';
};

const MasterDataEntityDrawer: FC<MasterDataEntityDrawerProps> = ({
  open,
  title,
  isEdit,
  formModel,
  formValues,
  loading = false,
  saving = false,
  canDelete = false,
  onClose,
  onSave,
  onDelete,
  onRequestDelete,
  onInvalid,
  children,
  saveLabel,
  width,
  skipValidation = false,
  deleteEntityName,
  dropdownData,
  onFieldChange,
  headerExtra,
}) => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));
  const formRef: RefObject<Form | null | undefined> = useRef();
  const drawerWidth = width ?? getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [entityNameForDelete, setEntityNameForDelete] = useState('');

  useEffect(() => {
    if (!open) {
      setDeleteModalOpen(false);
      setEntityNameForDelete('');
    }
  }, [open]);

  const handleSave = async () => {
    if (!formRef.current) return;
    const { getFormData } = formRef.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    if (!skipValidation && !isFormValid) {
      onInvalid?.(strings.required_error_prefix_message || 'Please fill the required fields.');
      return;
    }
    const body = removeErrorFieldsFromValues(formData);
    await onSave(body as FormDataModel);
  };

  const closeDeleteModal = () => setDeleteModalOpen(false);

  const openDeleteModal = () => {
    let liveValues = formValues;
    if (formRef.current) {
      const { getFormData } = formRef.current as {
        getFormData: () => { formData: FormDataModel; isFormValid: boolean };
      };
      liveValues = getFormData().formData || formValues;
    }
    const name = resolveEntityName(liveValues, deleteEntityName);
    if (onRequestDelete) {
      onRequestDelete(name);
      return;
    }
    setEntityNameForDelete(name);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!onDelete) return;
    setDeleteModalOpen(false);
    await onDelete();
  };

  return (
    <>
      <DrawerMenu
        open={open}
        handleClose={onClose}
        closeButton
        flexColumn
        noPadding
        overlapHeader
        title={title}
        width={drawerWidth}>
        {(loading || saving) && <PageLoader />}
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
          <Box sx={{ flex: 1, overflow: 'auto', px: 2, pt: 2, pb: 1 }}>
            {headerExtra ? <Box sx={{ mb: 2 }}>{headerExtra}</Box> : null}
            <Grid container spacing={2} alignItems="flex-start">
              <Form
                key={`${open}-${isEdit}-${Object.keys(formValues).join('-')}`}
                ref={formRef as any}
                model={formModel}
                values={formValues}
                dropdownData={dropdownData}
                onChange={(field, value, formData) => onFieldChange?.(field, value, formData)}
              />
            </Grid>
            {children}
          </Box>
          <Box
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              pt: 1.5,
              pb: 2,
              mt: 1,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}>
            <Box>
              {isEdit && canDelete && (onDelete || onRequestDelete) ? (
                <Button color="error" onClick={openDeleteModal} disabled={saving}>
                  {strings.deleteText}
                </Button>
              ) : null}
            </Box>
            <Stack direction="row" spacing={1}>
              <Button color="inherit" onClick={onClose} disabled={saving}>
                {strings.cancelText}
              </Button>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saveLabel || (isEdit ? 'Save Changes' : 'Save')}
              </Button>
            </Stack>
          </Box>
        </Box>
      </DrawerMenu>

      {onRequestDelete ? null : (
        <ConfirmModal
          open={deleteModalOpen}
          closeModal={closeDeleteModal}
          handleYes={confirmDelete}
          title={strings.deleteText}
          subtitle={strings.confirmDeletedText}
          entityName={entityNameForDelete || undefined}
          confirmLabel={strings.deleteText}
          cancelLabel={strings.cancelText}
          tone="danger"
          loading={saving}
        />
      )}
    </>
  );
};

export default MasterDataEntityDrawer;
